"use client";

import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import { X } from "lucide-react";
import { PLACEMENTS_DATA } from "@/data/placementDetails";
// PLACEMENTS_DATA shape: { name, title, companyLogo, image }
// — matches the existing Placements.jsx usage exactly.

// ─── Single card ──────────────────────────────────────────────────────────────
function StudentCard({
  name,
  title,
  companyLogo,
  companyName,
  image,
  compact = false,
}) {
  return (
    <div
      className={`placed-card flex-shrink-0 bg-white border border-[#CDE2EB] overflow-hidden flex flex-col ${
        compact ? "w-full" : "w-[220px]"
      }`}
    >
      {/* Portrait */}
      <div
        className={`relative w-full ${
          compact ? "h-[180px] sm:h-[220px]" : "h-[270px]"
        } overflow-hidden`}
      >
        <Image
          src={image}
          alt={name}
          fill
          className="object-cover object-top"
          sizes="(max-width: 768px) 100vw, 50vw"
        />
        <div
          className="absolute inset-0 flex flex-col justify-end px-3 py-3"
          style={{
            background:
              "linear-gradient(180deg, rgba(8,30,45,0) 45%, rgba(8,30,45,0.82) 100%)",
          }}
        >
          <h3 className="text-white font-bold text-[15px] leading-tight">
            {name}
          </h3>
          <p className="text-white/80 text-[13px]">{title}</p>
        </div>
      </div>

      {/* Company logo strip */}
      <div className="flex items-center justify-center h-[40px] px-2 sm:px-4 border-t border-[#CDE2EB] bg-white mt-auto">
        {companyLogo ? (
          <Image
            src={companyLogo}
            alt={`${name} company`}
            width={110}
            height={40}
            className="h-[24px] sm:h-[30px] w-auto object-contain"
          />
        ) : (
          <p
            className="text-xs md:text-sm  leading-tight"
            style={{ fontFamily: "saans-bold" }}
          >
            {companyName}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Infinite auto-scrolling row ──────────────────────────────────────────────
function InfiniteRow({ students, direction = "left", speed = 40 }) {
  const trackRef = useRef(null);
  const animRef = useRef(null);
  const posRef = useRef(0);
  const pausedRef = useRef(false);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    // Clone all originals for seamless looping
    const originals = Array.from(track.children);
    originals.forEach((card) => track.appendChild(card.cloneNode(true)));

    const step = () => {
      if (!pausedRef.current) {
        const delta = direction === "left" ? -(speed / 60) : speed / 60;
        posRef.current += delta;
        const half = track.scrollWidth / 2;
        if (posRef.current <= -half) posRef.current += half;
        if (posRef.current >= 0) posRef.current -= half;
        track.style.transform = `translateX(${posRef.current}px)`;
      }
      animRef.current = requestAnimationFrame(step);
    };

    animRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animRef.current);
  }, [direction, speed]);

  return (
    <div
      className="overflow-hidden w-full"
      onMouseEnter={() => {
        pausedRef.current = true;
      }}
      onMouseLeave={() => {
        pausedRef.current = false;
      }}
    >
      <div ref={trackRef} className="flex gap-4 w-max">
        {students.map((s, i) => (
          <StudentCard key={i} {...s} />
        ))}
      </div>
    </div>
  );
}

// ─── "View All" Modal ─────────────────────────────────────────────────────────
function AllPlacementsModal({ onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-3 md:p-4 z-[999]"
      style={{ background: "rgba(8,30,45,0.6)", backdropFilter: "blur(5px)" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bg-white w-full max-w-5xl rounded-2xl flex flex-col overflow-hidden"
        style={{ maxHeight: "90vh" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 md:px-6 md:py-5 border-b border-[#CDE2EB] flex-shrink-0">
          <div>
            <h2
              className="text-xl sm:text-3xl lg:text-4xl font-bold text-black"
              style={{ fontFamily: "saans-bold" }}
            >
              Student Placement Highlights
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition"
            aria-label="Close modal"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Scrollable grid */}
        <div className="overflow-y-auto flex-1 p-3 md:p-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
            {PLACEMENTS_DATA.map((student, i) => (
              <StudentCard key={i} {...student} compact />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────
const PlacedStudents = () => {
  const [modalOpen, setModalOpen] = useState(false);

  // Split into two rows for the two carousel tracks
  const mid = Math.ceil(PLACEMENTS_DATA.length / 2);
  const row1 = PLACEMENTS_DATA.slice(0, mid);
  const row2 = PLACEMENTS_DATA.slice(mid);

  return (
    <>
      <section className="py-10 md:py-14 overflow-hidden">
        {/* Section header */}
        <div className="page-container text-center mb-8">
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-black text-left"
            style={{ fontFamily: "saans-bold" }}
          >
            Student Placement Highlights
          </h2>
          <p className="text-sm md:text-base lg:text-lg text-black leading-relaxed w-full md:w-[80%] lg:w-[70%] mb-6 text-left">
            Each placement reflects a student’s journey from learning skills in
            classrooms and labs to applying them in real workplaces. These
            highlights share how students grow in confidence, gain industry
            exposure, and take their first steps into meaningful professional
            roles.
          </p>
        </div>

        {/* Carousel row 1 — scrolls left */}
        <div className="mb-4">
          <InfiniteRow students={row1} direction="left" speed={38} />
        </div>

        {/* Carousel row 2 — scrolls right */}
        <div className="mb-8">
          <InfiniteRow students={row2} direction="right" speed={38} />
        </div>

        {/* CTA button */}
        <div className="flex justify-center page-container">
          <button
            onClick={() => setModalOpen(true)}
            className="inline-flex items-center gap-2 px-8 py-3 bg-[#081E2D] text-white text-sm font-medium rounded-full hover:bg-[#0f2e44] transition-all duration-200 hover:scale-[1.03] active:scale-[0.98]"
          >
            View All Placements
          </button>
        </div>
      </section>

      {modalOpen && <AllPlacementsModal onClose={() => setModalOpen(false)} />}
    </>
  );
};

export default PlacedStudents;
